import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLang } from '@/context/LanguageContext';
import { MapPin, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public interactive world map showing member density per city/country.
 * Uses Leaflet (loaded via CDN in index.html). No PII: only shows count +
 * an anonymized sample list of (age, gender) — no name, no WhatsApp, no IG.
 */
export default function WorldMap() {
  const { lang } = useLang();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);

  // Load stats + refresh every 30s so total updates live.
  useEffect(() => {
    let alive = true;
    const fetchStats = async () => {
      try {
        const { data } = await axios.get(`${API}/public/stats`);
        if (alive) setStats(data);
      } catch (e) { /* ignore */ }
    };
    fetchStats();
    const id = setInterval(fetchStats, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Initialize Leaflet once, then re-render markers whenever stats change.
  useEffect(() => {
    if (!stats || !containerRef.current) return;
    const L = window.L;
    if (!L) {
      // Leaflet script not yet loaded — retry shortly.
      const t = setTimeout(() => setStats(s => ({ ...s })), 200);
      return () => clearTimeout(t);
    }
    if (!mapRef.current) {
      mapRef.current = L.map(containerRef.current, {
        center: [-2.5, 118], zoom: 4, minZoom: 2, maxZoom: 10,
        scrollWheelZoom: false, worldCopyJump: true,
      });
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
        subdomains: 'abcd', maxZoom: 20,
      }).addTo(mapRef.current);
      mapRef.current._pins = L.layerGroup().addTo(mapRef.current);
    }
    const layer = mapRef.current._pins;
    layer.clearLayers();
    (stats.pins || []).forEach(p => {
      const size = Math.max(20, Math.min(56, 18 + Math.log2(p.count + 1) * 8));
      const html = `
        <div style="width:${size}px;height:${size}px;border-radius:50%;background:rgba(217,138,148,0.85);border:2px solid #fff;color:#fff;font-weight:700;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(217,138,148,0.5);font-size:${Math.max(10, size / 3.5)}px;">${p.count}</div>`;
      const icon = L.divIcon({ html, className: 'mgi-pin', iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
      const marker = L.marker([p.lat, p.lng], { icon }).addTo(layer);
      marker.on('click', () => setSelected(p));
    });
  }, [stats]);

  return (
    <section id="world-map" className="py-14 sm:py-20 px-4 sm:px-6" style={{ borderTop: '1px solid var(--border-color)' }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-6 sm:mb-10">
          <span className="text-xs sm:text-sm uppercase tracking-[0.2em] sm:tracking-[0.3em] text-[#D98A94] font-medium">
            {lang === 'en' ? 'Live Community Map' : 'Peta Komunitas Live'}
          </span>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold t-text mt-2 sm:mt-3 tracking-tight font-romantic">
            {lang === 'en' ? 'Members Around the World' : 'Anggota di Seluruh Dunia'}
          </h2>
          <div className="mt-3 sm:mt-4 flex flex-wrap items-center justify-center gap-3 sm:gap-6" data-testid="live-stats">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full glass">
              <Users className="w-4 h-4 text-[#D98A94]" />
              <span className="text-sm t-text font-bold" data-testid="live-total">{stats?.total_members ?? '…'}</span>
              <span className="text-xs t-text-muted">{lang === 'en' ? 'total members' : 'total anggota'}</span>
              <span className="ml-1 w-2 h-2 rounded-full bg-green-500 animate-pulse" title="live" />
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full glass">
              <MapPin className="w-4 h-4 text-[#D98A94]" />
              <span className="text-sm t-text font-bold">{stats?.pins?.length ?? '…'}</span>
              <span className="text-xs t-text-muted">{lang === 'en' ? 'cities/countries' : 'kota/negara'}</span>
            </div>
          </div>
          <p className="mt-3 text-xs t-text-muted max-w-xl mx-auto">
            {lang === 'en'
              ? 'Click a pin to preview age, gender, occupation & community of members there. Name, WhatsApp and photo are hidden — join to unlock full contact.'
              : 'Klik pin untuk melihat usia, gender, pekerjaan & komunitas anggota di sana. Nama, WhatsApp, dan foto disembunyikan — join untuk mendapatkan kontak lengkap.'}
          </p>
        </div>

        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
          <div ref={containerRef} data-testid="world-map-container" style={{ height: 480, width: '100%' }} />
        </div>

        {selected && (
          <div className="mt-4 glass-card rounded-2xl p-4" data-testid="pin-detail">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="text-xs t-text-muted uppercase tracking-wide">{lang === 'en' ? 'Location' : 'Lokasi'}</div>
                <div className="text-lg font-bold t-text">{selected.city}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-xs t-text-muted hover:text-[#D98A94]">✕</button>
            </div>
            <div className="text-2xl font-bold text-[#D98A94] mb-3">{selected.count} {lang === 'en' ? 'members' : 'anggota'}</div>
            <div className="text-xs t-text-muted mb-2">{lang === 'en' ? 'Sample (first 20)' : 'Contoh (20 pertama)'}:</div>
            <div className="flex flex-col gap-2">
              {selected.samples.map((s, i) => (
                <div key={`${s.gender}-${s.age}-${s.category || ''}-${i}`} className="flex flex-wrap items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg" style={{ background: s.gender === 'female' ? 'rgba(217,138,148,0.10)' : 'rgba(37,99,235,0.08)', border: `1px solid ${s.gender === 'female' ? 'rgba(217,138,148,0.25)' : 'rgba(37,99,235,0.20)'}` }}>
                  <span className="font-bold px-1.5 py-0.5 rounded" style={{ background: s.gender === 'female' ? 'rgba(217,138,148,0.25)' : 'rgba(37,99,235,0.18)', color: s.gender === 'female' ? '#D98A94' : '#2563EB' }}>
                    {s.gender === 'female' ? (lang === 'en' ? 'F' : 'P') : (lang === 'en' ? 'M' : 'L')} · {s.age}
                  </span>
                  {s.category && (
                    <span className="t-text-secondary">
                      · <b className="t-text">{s.category}</b>
                    </span>
                  )}
                  {s.occupation && (
                    <span className="t-text-muted">
                      · {s.occupation}
                    </span>
                  )}
                </div>
              ))}
              {selected.samples.length === 0 && <span className="text-xs t-text-muted">{lang === 'en' ? 'No age data available.' : 'Data usia belum tersedia.'}</span>}
            </div>
            <div className="mt-4 pt-4 border-t t-border">
              <p className="text-xs t-text-secondary mb-3">
                {lang === 'en' ? 'To view names, WhatsApp, and Instagram — join as a member.' : 'Untuk melihat nama, WhatsApp, dan Instagram — silakan join sebagai member.'}
              </p>
              <a href="https://wa.me/6281318719105?text=Halo%20MGI%2C%20saya%20ingin%20join%20member" target="_blank" rel="noreferrer" data-testid="join-wa-cta"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#25D366]/90 text-white font-semibold text-sm px-5 py-2.5 rounded-full transition">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" /></svg>
                {lang === 'en' ? 'Join Member — WA 0813 1871 9105' : 'Join Member — WA 0813 1871 9105'}
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
